import {SetStateAction, useCallback} from "react";
import * as _ from "lodash-es";
import useComputedState from "./computedState";

function useProjectedValue<T, U>(
  value: T,
  setValue: (newValue: T) => void,
  project: (val: T) => U,
  unproject: (val: U) => T,
) {
  const [state, setState] = useComputedState<{value: T; projected: U}>(prevState => {
    if (prevState !== undefined && (prevState.value === value || _.isEqual(prevState.value, value))) {
      return prevState;
    }
    const projected = project(value);
    return {value, projected};
  });
  const setProjected = useCallback(
    (newProjected: SetStateAction<U>) => {
      const newState = setState(prevState => {
        const projected =
          typeof newProjected === "function" ? (newProjected as (prev: U) => U)(prevState.projected) : newProjected;
        let value = unproject(projected);
        if (value !== prevState.value && _.isEqual(value, prevState.value)) {
          value = prevState.value;
        }
        return {value, projected};
      });
      setValue(newState.value);
    },
    [setState, unproject, setValue],
  );
  return [state.projected, setProjected] as const;
}

export default useProjectedValue;
